import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type ReactNode } from "react";
import { cn } from "../../../lib/utils.js";

type SheetProps = React.ComponentProps<typeof DialogPrimitive.Root>;

/** Pass `modal={false}` when the sheet hosts portaled popovers (e.g. Radix Select). Default is modal. */
export function Sheet({ modal = true, ...props }: SheetProps): React.JSX.Element {
  return <DialogPrimitive.Root modal={modal} {...props} />;
}
export const SheetTrigger = DialogPrimitive.Trigger;
export const SheetClose = DialogPrimitive.Close;
export const SheetPortal = DialogPrimitive.Portal;
export const SheetTitle = DialogPrimitive.Title;
export const SheetDescription = DialogPrimitive.Description;

export const SheetOverlay = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn("fixed inset-0 z-[var(--z-overlay)] bg-ink/50 animate-overlay-in", className)}
    {...props}
  />
));
SheetOverlay.displayName = DialogPrimitive.Overlay.displayName;

export const sheetSideVariants = cva(
  "fixed z-[var(--z-sheet)] flex flex-col",
  {
    variants: {
      side: {
        end: "inset-y-0 end-0 w-[var(--sidebar-width)] max-w-[85vw] overflow-y-auto bg-sidebar animate-sheet-in-end rtl:animate-sheet-in-start data-[state=closed]:animate-sheet-out-end rtl:data-[state=closed]:animate-sheet-out-start",
        start:
          "inset-y-0 start-0 end-auto w-[var(--sidebar-width)] max-w-[85vw] overflow-y-auto bg-sidebar animate-sheet-in-start rtl:animate-sheet-in-end data-[state=closed]:animate-sheet-out-start rtl:data-[state=closed]:animate-sheet-out-end",
        /** Physical left edge in both LTR and RTL (does not mirror). */
        left:
          "inset-y-0 left-0 right-auto w-[var(--sidebar-width)] max-w-[85vw] overflow-y-auto bg-sidebar animate-sheet-in-start data-[state=closed]:animate-sheet-out-start",
        bottom:
          "inset-x-0 bottom-0 top-auto h-auto max-h-[min(50dvh,20rem)] w-full max-w-none overflow-y-auto rounded-t-[1.25rem] border border-b-0 border-line bg-surface-elevated text-foreground shadow-[0_-6px_24px_rgb(15_23_42/0.12)] animate-sheet-in-bottom",
      },
    },
    defaultVariants: {
      side: "end",
    },
  },
);

const sideVariants = sheetSideVariants;

export interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
    VariantProps<typeof sideVariants> {
  children: ReactNode;
  overlayClassName?: string;
}

export const SheetContent = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  SheetContentProps
>(({ className, overlayClassName, children, side = "end", ...props }, ref) => (
  <SheetPortal>
    <SheetOverlay
      className={cn(side === "bottom" ? "z-[var(--z-alert-overlay)]" : undefined, overlayClassName)}
    />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        sideVariants({ side }),
        side === "bottom" && "z-[var(--z-alert)]",
        className,
      )}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </SheetPortal>
));
SheetContent.displayName = DialogPrimitive.Content.displayName;
